import type { DecisionEventType } from "../domain/decisionEventTypes";
import type { DemoOutputQueue } from "../domain/ruleTypes";
import type { RuleIntegrationStatus } from "../domain/ruleCatalog";
import {
    getRuleRoutingOutcome,
    renderRuleRoutingActionLines,
    type RuleOutcomeKey,
    type RuleRoutingOutcome,
} from "../domain/ruleRouting";

export type { RuleOutcomeKey, RuleRoutingOutcome as RuleOutcomeRouting } from "../domain/ruleRouting";

export type RuleLogicImplementation =
    | "statement_rule"
    | "rule_table"
    | "value_table"
    | "mapping"
    | "text_merge"
    | "flow_route"
    | "expression";

export type RuleLogicOperator =
    | "=="
    | "!="
    | "in"
    | "not_in"
    | ">"
    | ">="
    | "<"
    | "<="
    | "exists"
    | "matches"
    | "not_contains"
    | "changed"
    | "lookup";

export type RuleLogicCondition = {
    id: string;
    field: string;
    operator: RuleLogicOperator;
    value?: string | number | string[];
    lookupTable?: string;
    label?: string;
};

export type RuleLogicRequirement = {
    id: string;
    fields: string[];
};

export type RuleLogicAction = {
    id: string;
    label: string;
    outputQueue?: DemoOutputQueue;
};

export type DemoRuleLogicDefinition = {
    ruleId: string;
    integrationSourceRuleId?: string;
    trigger: DecisionEventType;
    sourceTable: string;
    implementation: RuleLogicImplementation;
    integrationStatus: RuleIntegrationStatus;
    targetAppGlueObjectLabel: string;
    appGlueStorageData?: object;
    conditions: RuleLogicCondition[];
    requirements: RuleLogicRequirement[];
    actions: RuleLogicAction[];
    outcomeRouting?: RuleRoutingOutcome[];
};

export type RuleLogicDefinitionMap = Record<string, DemoRuleLogicDefinition>;

export const IMPLEMENTATION_LABELS: Record<RuleLogicImplementation, string> = {
    statement_rule: "Statement Rule",
    rule_table: "Rule Table",
    value_table: "Value Table",
    mapping: "Mapping",
    text_merge: "Text Merge",
    flow_route: "Flow Route",
    expression: "Expression",
};

export const FIELD_LABELS: Record<string, string> = {
    category: "category",
    package_type: "package_type",
    pin_count: "pin_count",
    voltage_range: "voltage_range",
    voltage_min: "voltage_min",
    voltage_max: "voltage_max",
    rohs_status: "rohs_status",
    lifecycle_status: "lifecycle_status",
    mpn: "MPN",
    taxonomy_path: "taxonomy_path",
    manufacturer_name: "manufacturer_name",
    price_change_pct: "price_change_pct",
    confidence_score: "confidence_score",
    source_hierarchy: "source hierarchy",
    prior_compliance_status: "prior compliance status",
    current_compliance_status: "current compliance status",
    master_catalog: "master catalog",
    open_activity: "open opportunities and purchase orders",
};

export const normalizeRuleField = (field: string): string => {
    const normalized = field.trim().toLowerCase().replace(/\s+/g, "_");
    if (normalized === "voltage" || normalized === "voltage_range" || normalized === "operating_voltage_range") {
        return "voltage_range";
    }
    if (normalized === "rohs" || normalized === "rohs_status" || normalized === "compliance_status") {
        return "rohs_status";
    }
    return normalized;
};

export const formatRuleField = (field: string): string => FIELD_LABELS[normalizeRuleField(field)] ?? field;

const formatConditionValue = (value: RuleLogicCondition["value"]): string => {
    if (Array.isArray(value)) return value.join(", ");
    return value === undefined ? "" : String(value);
};

const renderConditionLine = (condition: RuleLogicCondition): string => {
    const field = formatRuleField(condition.field);
    const value = formatConditionValue(condition.value);

    if (condition.operator === "lookup") {
        return `LOOKUP ${condition.lookupTable ?? value} by ${field}`;
    }
    if (condition.operator === "matches") {
        return `CHECK ${field} matches ${value}`;
    }
    if (condition.operator === "not_contains") {
        return `CHECK ${field} excludes ${value}`;
    }
    if (condition.operator === "changed") {
        return `COMPARE ${field} to ${value}`;
    }
    if (condition.operator === "exists") {
        return `CHECK ${field} exists`;
    }
    if (condition.operator === "in") {
        return `IF ${field} in ${value}`;
    }
    if (condition.operator === "not_in") {
        return `IF ${field} not in ${value}`;
    }
    return `IF ${field} ${condition.operator} ${value}`;
};

export const renderRuleLogicLines = (definition: DemoRuleLogicDefinition): string[] => [
    ...definition.conditions.map(renderConditionLine),
    ...definition.requirements
        .filter((requirement) => requirement.fields.length > 0)
        .map((requirement) => `REQUIRE ${requirement.fields.map(formatRuleField).join(", ")}`),
    ...(definition.outcomeRouting?.length
        ? renderRuleRoutingActionLines(definition.outcomeRouting)
        : definition.actions.map((action) => `ACTION ${action.label}`)),
];

export const cloneRuleLogicDefinition = (definition: DemoRuleLogicDefinition): DemoRuleLogicDefinition =>
    JSON.parse(JSON.stringify(definition)) as DemoRuleLogicDefinition;

export const cloneRuleLogicDefinitionMap = (
    definitions: RuleLogicDefinitionMap = SEEDED_RULE_LOGIC_BY_ID,
): RuleLogicDefinitionMap =>
    Object.fromEntries(
        Object.entries(definitions).map(([ruleId, definition]) => [ruleId, cloneRuleLogicDefinition(definition)]),
    );

export const getRuleLogicDefinition = (definitions: RuleLogicDefinitionMap, ruleId: string): DemoRuleLogicDefinition =>
    definitions[ruleId] ?? SEEDED_RULE_LOGIC_BY_ID[ruleId]!;

export const getRuleIntegrationSourceRuleId = (definition: DemoRuleLogicDefinition): string =>
    definition.integrationSourceRuleId ?? definition.ruleId;

export const getRouteOutputQueue = (
    definition: DemoRuleLogicDefinition,
    fallback?: DemoOutputQueue,
): DemoOutputQueue | undefined => definition.actions.find((action) => action.outputQueue)?.outputQueue ?? fallback;

export const getOutcomeRouting = (
    definition: DemoRuleLogicDefinition | undefined,
    when: RuleOutcomeKey,
): RuleRoutingOutcome | undefined => getRuleRoutingOutcome(definition?.outcomeRouting, when);

export const SEEDED_RULE_LOGIC_BY_ID: RuleLogicDefinitionMap = {
    "DQ-001": {
        ruleId: "DQ-001",
        trigger: "part_enrichment_received",
        sourceTable: "enriched_parts",
        implementation: "statement_rule",
        integrationStatus: "native_appglue",
        targetAppGlueObjectLabel: "StatementRuleConfiguration + ExpressionConfiguration",
        conditions: [{ id: "category-semiconductor", field: "category", operator: "==", value: "Semiconductor" }],
        requirements: [
            {
                id: "required-semiconductor-fields",
                fields: ["package_type", "pin_count", "voltage_range", "rohs_status"],
            },
        ],
        actions: [
            { id: "quarantine-incomplete", label: "quarantine incomplete rows", outputQueue: "quarantine_records" },
        ],
        outcomeRouting: [
            {
                id: "dq001-pass-routing",
                when: "pass",
                status: "pass",
                label: "allow complete rows",
                reasonCode: "COMPLETE",
                explanation: "Required attributes are complete.",
            },
            {
                id: "dq001-fail-routing",
                when: "fail",
                status: "rejected",
                label: "quarantine incomplete rows",
                outputQueue: "quarantine_records",
                reasonCode: "MISSING_REQUIRED_ATTRIBUTES",
                explanation: "A matching row is missing required publish fields.",
            },
            {
                id: "dq001-error-routing",
                when: "error",
                status: "rejected",
                label: "hold incomplete expression",
                outputQueue: "pending_review",
                reasonCode: "APPGLUE_EXPRESSION_INCOMPLETE",
                explanation:
                    "The AppGlue expression is incomplete, so validation preview is holding rows until the expression is completed.",
            },
        ],
    },
    "DQ-002": {
        ruleId: "DQ-002",
        trigger: "part_enrichment_received",
        sourceTable: "enriched_parts",
        implementation: "statement_rule",
        integrationStatus: "native_appglue",
        targetAppGlueObjectLabel: "StatementRuleConfiguration + ExpressionConfiguration",
        conditions: [{ id: "passive-category", field: "category", operator: "in", value: ["Capacitor", "Resistor"] }],
        requirements: [],
        actions: [
            { id: "normalize-units", label: "normalize source units" },
            { id: "route-conflicts", label: "route unresolved conflicts to review", outputQueue: "pending_review" },
        ],
    },
    "DQ-003": {
        ruleId: "DQ-003",
        trigger: "lifecycle_status_change",
        sourceTable: "enriched_parts",
        implementation: "statement_rule",
        integrationStatus: "native_appglue",
        targetAppGlueObjectLabel: "StatementRuleConfiguration + ExpressionConfiguration",
        conditions: [{ id: "blocked-lifecycle", field: "lifecycle_status", operator: "in", value: ["EOL", "NRND"] }],
        requirements: [],
        actions: [
            { id: "archive-part", label: "archive part", outputQueue: "archived_parts" },
            { id: "block-active-catalog", label: "block active catalog publish" },
        ],
        outcomeRouting: [
            {
                id: "dq003-pass-routing",
                when: "pass",
                status: "pass",
                label: "allow active catalog publish",
                outputQueue: "published_catalog_candidates",
                reasonCode: "LIFECYCLE_ALLOWED",
                explanation: "Lifecycle status is allowed for active catalog publish.",
            },
            {
                id: "dq003-fail-routing",
                when: "fail",
                status: "rejected",
                label: "archive part",
                outputQueue: "archived_parts",
                reasonCode: "LIFECYCLE_BLOCKED",
                explanation: "Lifecycle status blocks active catalog publish and routes the part to archive.",
            },
            {
                id: "dq003-error-routing",
                when: "error",
                status: "rejected",
                label: "hold incomplete expression",
                outputQueue: "pending_review",
                reasonCode: "APPGLUE_EXPRESSION_INCOMPLETE",
                explanation:
                    "The AppGlue expression is incomplete, so validation preview is holding rows until the expression is completed.",
            },
        ],
    },
    "DQ-004": {
        ruleId: "DQ-004",
        trigger: "part_enrichment_received",
        sourceTable: "enriched_parts",
        implementation: "statement_rule",
        integrationStatus: "native_appglue",
        targetAppGlueObjectLabel: "StatementRuleConfiguration + ExpressionConfiguration",
        conditions: [
            { id: "mpn-pattern", field: "mpn", operator: "matches", value: "manufacturer pattern" },
            {
                id: "mpn-placeholder",
                field: "mpn",
                operator: "not_contains",
                value: "placeholder and whitespace cases",
            },
        ],
        requirements: [],
        actions: [{ id: "quarantine-mpn", label: "quarantine suspicious MPNs", outputQueue: "quarantine_records" }],
    },
    "TR-005": {
        ruleId: "TR-005",
        trigger: "part_enrichment_received",
        sourceTable: "enriched_parts",
        implementation: "value_table",
        integrationStatus: "adapter_backed",
        targetAppGlueObjectLabel: "ValueTableConfiguration",
        conditions: [
            {
                id: "taxonomy-lookup",
                field: "taxonomy_path",
                operator: "lookup",
                lookupTable: "taxonomy_product_mapping",
            },
        ],
        requirements: [],
        actions: [
            {
                id: "set-erp-group",
                label: "set ERP product group and product line",
                outputQueue: "published_catalog_candidates",
            },
            { id: "set-crm-family", label: "set CRM product family" },
        ],
    },
    "TR-006": {
        ruleId: "TR-006",
        trigger: "part_enrichment_received",
        sourceTable: "enriched_parts",
        implementation: "text_merge",
        integrationStatus: "native_appglue",
        targetAppGlueObjectLabel: "TextMergeConfiguration",
        conditions: [{ id: "description-inputs", field: "package_type", operator: "exists" }],
        requirements: [
            {
                id: "description-priority-fields",
                fields: ["manufacturer_name", "mpn", "voltage_range", "package_type"],
            },
        ],
        actions: [
            {
                id: "truncate-secondary",
                label: "truncate secondary fragments first",
                outputQueue: "epicor_description_previews",
            },
        ],
        outcomeRouting: [
            {
                id: "tr006-pass-routing",
                when: "pass",
                status: "pass",
                label: "publish ERP description preview",
                outputQueue: "epicor_description_previews",
                reasonCode: "DESCRIPTION_READY",
                explanation:
                    "ERP description preview was built from the saved AppGlue text merge and fit to the downstream field limit.",
            },
            {
                id: "tr006-fail-routing",
                when: "fail",
                status: "flagged",
                label: "hold incomplete description inputs",
                outputQueue: "pending_review",
                reasonCode: "DESCRIPTION_INPUT_INCOMPLETE",
                explanation: "Description formatting is held because priority product attributes are missing.",
            },
            {
                id: "tr006-error-routing",
                when: "error",
                status: "rejected",
                label: "hold text merge runtime error",
                outputQueue: "pending_review",
                reasonCode: "TEXT_MERGE_RUNTIME_ERROR",
                explanation: "The AppGlue text merge could not produce a usable description preview.",
            },
        ],
    },
    "TR-007": {
        ruleId: "TR-007",
        trigger: "part_enrichment_received",
        sourceTable: "enriched_parts",
        implementation: "text_merge",
        integrationStatus: "native_appglue",
        targetAppGlueObjectLabel: "TextMergeConfiguration",
        conditions: [{ id: "mpn-readable", field: "mpn", operator: "not_contains", value: "placeholder values" }],
        requirements: [
            { id: "product-name-fields", fields: ["manufacturer_name", "mpn", "package_type", "voltage_range"] },
        ],
        actions: [
            {
                id: "preview-crm-name",
                label: "preview CRM product name",
                outputQueue: "salesforce_product_name_previews",
            },
        ],
    },
    "TR-008": {
        ruleId: "TR-008",
        trigger: "supplier_price_update_received",
        sourceTable: "supplier_price_updates",
        implementation: "rule_table",
        integrationStatus: "adapter_backed",
        targetAppGlueObjectLabel: "RuleTableConfiguration",
        conditions: [
            {
                id: "price-rule-lookup",
                field: "manufacturer_name",
                operator: "lookup",
                lookupTable: "price_tolerance_rules",
            },
            { id: "price-review-threshold", field: "price_change_pct", operator: ">", value: "review threshold" },
        ],
        requirements: [],
        actions: [
            {
                id: "approve-within-threshold",
                label: "approve changes within threshold",
                outputQueue: "epicor_cost_file_updates",
            },
            {
                id: "hold-above-review",
                label: "hold breaches for procurement review",
                outputQueue: "procurement_review_queue",
            },
        ],
    },
    "WF-009": {
        ruleId: "WF-009",
        trigger: "part_enrichment_received",
        sourceTable: "enriched_parts",
        implementation: "rule_table",
        integrationStatus: "adapter_backed",
        targetAppGlueObjectLabel: "RuleTableConfiguration",
        conditions: [
            {
                id: "catalog-lookup",
                field: "master_catalog",
                operator: "lookup",
                lookupTable: "snowflake_master_catalog",
            },
        ],
        requirements: [],
        actions: [
            {
                id: "create-product-event",
                label: "create CRM product-review event",
                outputQueue: "salesforce_product_create_events",
            },
            { id: "assign-product-owner", label: "assign product owner review" },
        ],
    },
    "WF-010": {
        ruleId: "WF-010",
        trigger: "supplier_price_update_received",
        sourceTable: "supplier_price_updates",
        implementation: "rule_table",
        integrationStatus: "adapter_backed",
        targetAppGlueObjectLabel: "RuleTableConfiguration",
        conditions: [
            { id: "material-price-increase", field: "price_change_pct", operator: ">", value: 15 },
            { id: "open-opportunity", field: "open_activity", operator: "exists" },
        ],
        requirements: [],
        actions: [
            {
                id: "margin-risk-alert",
                label: "create CRM margin-risk alert",
                outputQueue: "salesforce_price_alert_events",
            },
        ],
    },
    "WF-011": {
        ruleId: "WF-011",
        trigger: "lifecycle_status_change",
        sourceTable: "enriched_parts",
        implementation: "rule_table",
        integrationStatus: "adapter_backed",
        targetAppGlueObjectLabel: "RuleTableConfiguration",
        conditions: [
            { id: "obsolete-lifecycle", field: "lifecycle_status", operator: "in", value: ["EOL", "NRND"] },
            { id: "open-commercial-activity", field: "open_activity", operator: "exists" },
        ],
        requirements: [],
        actions: [
            {
                id: "replacement-alert",
                label: "alert with replacement part when available",
                outputQueue: "salesforce_obsolescence_alert_events",
            },
        ],
    },
    "WF-012": {
        ruleId: "WF-012",
        trigger: "compliance_change_detected",
        sourceTable: "enriched_parts",
        implementation: "rule_table",
        integrationStatus: "adapter_backed",
        targetAppGlueObjectLabel: "RuleTableConfiguration",
        conditions: [
            {
                id: "compliance-changed",
                field: "prior_compliance_status",
                operator: "changed",
                value: "current compliance status",
            },
        ],
        requirements: [],
        actions: [
            {
                id: "hold-compliance-overwrite",
                label: "hold compliance overwrite for review",
                outputQueue: "compliance_review_queue",
            },
        ],
    },
    "CF-013": {
        ruleId: "CF-013",
        trigger: "part_enrichment_received",
        sourceTable: "enriched_parts",
        implementation: "statement_rule",
        integrationStatus: "native_appglue",
        targetAppGlueObjectLabel: "StatementRuleConfiguration + ExpressionConfiguration",
        conditions: [{ id: "confidence-threshold", field: "confidence_score", operator: ">=", value: 85 }],
        requirements: [],
        actions: [
            { id: "publish-high-confidence", label: "publish candidate", outputQueue: "published_catalog_candidates" },
            {
                id: "hold-low-confidence",
                label: "route low-confidence rows to pending review",
                outputQueue: "pending_review",
            },
        ],
        outcomeRouting: [
            {
                id: "cf013-pass-routing",
                when: "pass",
                status: "pass",
                label: "publish candidate",
                outputQueue: "published_catalog_candidates",
                reasonCode: "CONFIDENCE_ACCEPTED",
                explanation: "Enrichment confidence meets the publish threshold.",
            },
            {
                id: "cf013-fail-routing",
                when: "fail",
                status: "flagged",
                label: "route low-confidence rows to pending review",
                outputQueue: "pending_review",
                reasonCode: "LOW_CONFIDENCE",
                explanation: "Enrichment confidence is below threshold, so downstream publish is paused.",
            },
            {
                id: "cf013-error-routing",
                when: "error",
                status: "rejected",
                label: "hold incomplete expression",
                outputQueue: "pending_review",
                reasonCode: "APPGLUE_EXPRESSION_INCOMPLETE",
                explanation:
                    "The AppGlue expression is incomplete, so validation preview is holding rows until the expression is completed.",
            },
        ],
    },
    "CF-014": {
        ruleId: "CF-014",
        trigger: "part_enrichment_received",
        sourceTable: "enriched_parts",
        implementation: "rule_table",
        integrationStatus: "adapter_backed",
        targetAppGlueObjectLabel: "RuleTableConfiguration",
        conditions: [
            {
                id: "source-priority",
                field: "source_hierarchy",
                operator: "lookup",
                lookupTable: "manufacturer_source_priority",
            },
        ],
        requirements: [],
        actions: [
            { id: "rank-sources", label: "rank manufacturer direct over enrichment and supplier-submitted" },
            { id: "log-overrides", label: "log source override decisions", outputQueue: "source_override_audit" },
        ],
    },
};
